import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogContent, TextField, Tooltip, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import droplet from '../../assets/droplet.png';
import NewYears from '../../assets/newYears.png';
import {BallIconBlack, BallIconWhite, CorrectIcon, DropTipsIcon, TinyDropIcon} from '../../icons';
import { getLocalStorage } from '../../util/storage';
import { useMatomoTrackingPoint } from '../../hooks/useMatomoTrackingPoint';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {RootState} from '../../store';

import WaterDropBag from './waterDropBag';
import PaymentRules from './paymentRules';
import MemberIdentityDialog from './memberIdentityDialog';

import { addMonthsWithMonthEndClamp, formatYmdFromDate } from '../../util/date';
import {ImagePricingRule, PendingTaskType} from '../../types/member.type';
import {MembershipQueueItem, MembershipQueueResponse} from '../../api/payment';

import memberDiscount from '../../assets/memberDiscount.png'
import {usePendingTask} from "../../hooks/usePendingTask";
import AnimatedNumber from '../../util/AnimatedNumber';

type PlanCard = {
  plan: string;
  membershipTier: string;
  productId?: string;
  origin_price: number;
  price: number;
  discount: number;
  introduce: string;
  function: string[];
  total: number;
  color: string;
  buttonColor: string;
  totalBg: string;
  shawdowColor: string;
  unit: string;
  dropDiscount: string;
};

type MembershipTier = 'free' | 'plus' | 'plus_year' | 'pro' | 'pro_year' | 'max' | 'max_year';

type PricingTierMap = Partial<Record<MembershipTier, number>>;

const MEMBERSHIP_PRODUCT_SLOTS = {
  PLUS_MONTH: 'membership_plus_month',
  PLUS_YEAR: 'membership_plus',
  PRO_MONTH: 'membership_pro_month',
  PRO_YEAR: 'membership_pro',
  MAX_MONTH: 'membership_max_month',
  MAX_YEAR: 'membership_max',
} as const;

type ModelPriceItem = {
  label: string;
  originalPrice: number;
  currentPrice: number;
};

const DISPLAY_MODEL_ORDER = [
  'Nano Banana 2',
  'Nano Banana Pro',
  'GPT Image 2.5 标准',
  'GPT Image 2.5 精细',
  'GPT Image 2.5 超精',
  'GPT Image 2.5 xhigh',
  'GPT Image 2.5 max',
  'GPT Image 2 标准',
  'GPT Image 2 精细',
  'GPT Image 2 超精',
  'Seedream 5.0 Pro',
  '图层分离',
  '超清放大'
] as const;

const getLatestTierPricing = (tiers?: Array<{ cost: PricingTierMap; effective_at: string }>): PricingTierMap => {
  if (!tiers?.length) return {};
  const now = Date.now();
  for (let i = tiers.length - 1; i >= 0; i--) {
    if (new Date(tiers[i].effective_at).getTime() <= now) {
      return tiers[i].cost ?? {};
    }
  }
  return tiers[0]?.cost ?? {};
};

const getLatestBaseCost = (costs?: Array<{ cost: number; effective_at: string }>): number => {
  if (!costs?.length) return 0;
  const now = Date.now();
  for (let i = costs.length - 1; i >= 0; i--) {
    if (new Date(costs[i].effective_at).getTime() <= now) {
      return costs[i].cost;
    }
  }
  return costs[0]?.cost ?? 0;
};

const getResolutionTierPrice = (rule: ImagePricingRule | undefined, membershipTier: MembershipTier): ModelPriceItem | null => {
  const resolution = rule?.resolutions?.['1K'];
  if (!rule || !resolution) return null;

  const tierPricing = getLatestTierPricing(resolution.cost_tier);
  return {
    label: rule.name,
    originalPrice: tierPricing.free ?? getLatestBaseCost(resolution.cost),
    currentPrice: tierPricing[membershipTier] ?? tierPricing.free ?? getLatestBaseCost(resolution.cost),
  };
};

const getQualityTierPrice = (
  rule: ImagePricingRule | undefined,
  qualityKey: 'low' | 'medium' | 'high' | 'xhigh' | 'max',
  label: string,
  membershipTier: MembershipTier
): ModelPriceItem | null => {
  const resolution = rule?.qualitys?.[qualityKey]?.resolutions?.['1K'];
  if (!rule || !resolution) return null;

  const tierPricing = getLatestTierPricing(resolution.cost_tier);
  return {
    label,
    originalPrice: tierPricing.free ?? getLatestBaseCost(resolution.cost),
    currentPrice: tierPricing[membershipTier] ?? tierPricing.free ?? getLatestBaseCost(resolution.cost),
  };
};

export default function PaymentMainDialog(props: {
  open: boolean;
  onClose: () => void;
  reportStayTime: (tab: number, recount?: boolean) => void;
  packages: number;
  setPackages: (tab: number) => void;

  // 水滴/账单弹窗
  setOpenBillDialog: (open: boolean) => void;
  setOpenDropWaterDialog: (open: boolean) => void;

  // 下单/弹窗联动
  setCallBackPlan: (plan: { plan: string; amount: number; dropAmount: number; productId?: string }) => void;
  setSelectPaymentOpen: (open: boolean) => void;
  setCheckBoxDialogOpen: (open: boolean) => void;
  handleSelectPaymentOpen: (open: boolean) => void;
  handleCallBackPlan: (plan: { plan: string; amount: number; dropAmount?: number }) => void;

  // 礼品码
  giftCode: string;
  setGiftCode: (code: string) => void;
  redeemGiftCode: () => void;
  RedeemMessage: string;

  // 会员队列
  membershipQueue: MembershipQueueItem[];
  // 会员队列原始数据
  membershipQueueRaw?: MembershipQueueResponse['data'] | null;
}) {
  const {
    open,
    onClose,
    reportStayTime,
    packages,
    setPackages,
    setOpenBillDialog,
    setOpenDropWaterDialog,
    setCallBackPlan,
    setSelectPaymentOpen,
    setCheckBoxDialogOpen,
    handleSelectPaymentOpen,
    handleCallBackPlan,
    giftCode,
    setGiftCode,
    redeemGiftCode,
    RedeemMessage,
    membershipQueue,
    membershipQueueRaw,
  } = props;

  const [memberIdentityOpen, setMemberIdentityOpen] = useState(false);

  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const memberInfo = useSelector((state: RootState) => state.member.memberInfo);
  const imagePricingRules = useSelector((state: RootState) => state.member.imagePricingRules);
  const storefrontProducts = useSelector((state: RootState) => state.waterdrop.products);

  const productInfoMap = useMemo(() => {
    const map: Record<string, { productId: string; name: string; introduce: string; description: string[]; originalPrice: number; price: number; discount: number; total: number }> = {};
    for (const p of storefrontProducts) {
      if (p.product_type === 'membership' && p.product_group_key) {
        map[p.product_group_key] = {
          productId: p.product_id,
          name: p.name || '',
          introduce: p.introduce || '',
          description: p.description || [],
          originalPrice: p.original_price / 100,
          price: p.price / 100,
          discount: Number(p.original_price_discount) || 0,
          total: p.member_drops,
        };
      }
    }
    return map;
  }, [storefrontProducts]);

  const hasMonthlyDrops = usePendingTask(PendingTaskType.MEMBER_MONTHLY_DROPS_GRANT);
  const hasGiftCodeRedeem = usePendingTask(PendingTaskType.GIFT_CODE_REDEEM);
  const hasRenewalActivate = usePendingTask(PendingTaskType.MEMBERSHIP_RENEWAL_ACTIVATE);


  const {
    trackConsumptionRecordClick,
    trackWaterdropDetailClick,
    trackBillingPopupShow,
    trackYearlySubscriptionPopupShow,
    trackSubscriptionPlanSelect,
    trackMemberIdentityClick,
  } = useMatomoTrackingPoint();


  const plans: PlanCard[] = useMemo(
    () => [
      {
        plan: t('free_plan'),
        membershipTier: 'free',
        productId: undefined,
        origin_price: 0,
        price: 0,
        discount: 0,
        introduce: '基础功能免费用，适合新手体验',
        function: ['基础图片生成与导出', '公共生成通道', '社区交流支持', '标准水滴消耗，无减免'],
        total: 0,
        color: 'var(--c-text-primary)',
        buttonColor: 'var(--c-border)',
        totalBg: 'var(--c-bg-secondary)',
        shawdowColor: 'var(--c-border)',
        unit: '月',
        dropDiscount: '+0%'
      },
      {
        plan: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_MONTH]?.name || '',
        membershipTier: 'plus',
        productId: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_MONTH]?.productId,
        origin_price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_MONTH]?.originalPrice || 0,
        price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_MONTH]?.price || 0,
        discount: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_MONTH]?.discount || 0,
        introduce: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_MONTH]?.introduce || '',
        function: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_MONTH]?.description || [],
        total: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_MONTH]?.total || 0,
        color: 'var(--c-accent-light)',
        buttonColor: 'var(--c-accent-light)',
        totalBg: 'var(--c-accent-bg)',
        shawdowColor: 'var(--c-accent-light)',
        unit: '月',
        dropDiscount: '+20%'
      },
      {
        plan: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_MONTH]?.name || '',
        membershipTier: 'pro',
        productId: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_MONTH]?.productId,
        origin_price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_MONTH]?.originalPrice || 0,
        price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_MONTH]?.price || 0,
        discount: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_MONTH]?.discount || 0,
        introduce: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_MONTH]?.introduce || '',
        function: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_MONTH]?.description || [],
        total: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_MONTH]?.total || 0,
        color: 'var(--c-brand-light)',
        buttonColor: 'var(--c-brand-light)',
        totalBg: '#FEE0FF',
        shawdowColor: 'var(--c-brand-light)',
        unit: '月',
        dropDiscount: '+50%'
      },
      {
        plan: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_MONTH]?.name || '',
        membershipTier: 'max',
        productId: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_MONTH]?.productId,
        origin_price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_MONTH]?.originalPrice || 0,
        price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_MONTH]?.price || 0,
        discount: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_MONTH]?.discount || 0,
        introduce: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_MONTH]?.introduce || '',
        function: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_MONTH]?.description || [],
        total: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_MONTH]?.total || 0,
        color: 'linear-gradient(to right, var(--c-grad-start), var(--c-brand-light), var(--c-grad-end))',
        buttonColor: 'linear-gradient(to right, var(--c-grad-start), var(--c-brand-light), var(--c-grad-end))',
        totalBg: '#E9CEFF',
        shawdowColor: 'var(--c-violet-glow)',
        unit: '月',
        dropDiscount: '+50%'
      },
    ],
    [t, productInfoMap]
  );

  const plansYears: PlanCard[] = useMemo(
    () => [
      {
        plan: productInfoMap['free_year']?.name || '',
        membershipTier: 'free_year',
        productId: productInfoMap['free_year']?.productId,
        origin_price: productInfoMap['free_year']?.originalPrice || 0,
        price: productInfoMap['free_year']?.price || 0,
        discount: productInfoMap['free_year']?.discount || 0,
        introduce: productInfoMap['free_year']?.introduce || '',
        function: productInfoMap['free_year']?.description || [],
        total: productInfoMap['free_year']?.total || 0,
        color: 'var(--c-text-primary)',
        buttonColor: 'var(--c-border)',
        totalBg: 'var(--c-bg-secondary)',
        shawdowColor: 'var(--c-border)',
        unit: '年',
        dropDiscount: '+0%'
      },
      {
        plan: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_YEAR]?.name || '',
        membershipTier: 'plus_year',
        productId: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_YEAR]?.productId,
        origin_price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_YEAR]?.originalPrice || 0,
        price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_YEAR]?.price || 0,
        discount: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_YEAR]?.discount || 0,
        introduce: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_YEAR]?.introduce || '',
        function: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_YEAR]?.description || [],
        total: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PLUS_YEAR]?.total || 0,
        color: 'var(--c-accent-light)',
        buttonColor: 'var(--c-accent-light)',
        totalBg: 'var(--c-accent-bg)',
        shawdowColor: 'var(--c-accent-light)',
        unit: '年',
        dropDiscount: '+50%'
      },
      {
        plan: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_YEAR]?.name || '',
        membershipTier: 'pro_year',
        productId: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_YEAR]?.productId,
        origin_price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_YEAR]?.originalPrice || 0,
        price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_YEAR]?.price || 0,
        discount: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_YEAR]?.discount || 0,
        introduce: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_YEAR]?.introduce || '',
        function: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_YEAR]?.description || [],
        total: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.PRO_YEAR]?.total || 0,
        color: 'var(--c-brand-light)',
        buttonColor: 'var(--c-brand-light)',
        totalBg: '#FEE0FF',
        shawdowColor: 'var(--c-brand-light)',
        unit: '年',
        dropDiscount: '+50%'
      },
      {
        plan: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_YEAR]?.name || '',
        membershipTier: 'max_year',
        productId: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_YEAR]?.productId,
        origin_price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_YEAR]?.originalPrice || 0,
        price: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_YEAR]?.price || 0,
        discount: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_YEAR]?.discount || 0,
        introduce: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_YEAR]?.introduce || '',
        function: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_YEAR]?.description || [],
        total: productInfoMap[MEMBERSHIP_PRODUCT_SLOTS.MAX_YEAR]?.total || 0,
        color: 'linear-gradient(to right, var(--c-grad-start), var(--c-brand-light), var(--c-grad-end))',
        buttonColor: 'linear-gradient(to right, var(--c-grad-start), var(--c-brand-light), var(--c-grad-end))',
        totalBg: '#E9CEFF',
        shawdowColor: 'var(--c-violet-glow)',
        unit: '年',
        dropDiscount: '+50%'
      },
    ],
    [t, productInfoMap]
  );

  const TIER_ORDER = useMemo(() => ['free', 'plus', 'pro', 'max', 'plus_year', 'pro_year', 'max_year'] as const, []);

  const memberIndex = useCallback(() => {
    return memberInfo ? TIER_ORDER.indexOf(memberInfo.membershipTier as (typeof TIER_ORDER)[number]) : 0;
  }, [TIER_ORDER, memberInfo]);

  const currentMember = useCallback(() => {
    return plans.concat(plansYears).find((plan) => plan.membershipTier === memberInfo?.membershipTier);
  }, [memberInfo?.membershipTier, plans, plansYears]);

  const queueMonths = membershipQueue
      .filter(item => item.status === 'queued' && item.tier === memberInfo?.membershipTier);

  const hasMembershipQueue = (membershipQueueRaw?.queue?.filter(item => item.status === 'queued')?.length ?? 0) > 0;

  const canClickIdentity = !!memberInfo && memberInfo.membershipTier !== 'free';

  const currentMemberAllExpireDate = memberInfo?.currentMemberEndTime
      ? (() => {
          let date = formatYmdFromDate(new Date(memberInfo.currentMemberEndTime));
          for (const item of queueMonths) {
              const months = item.tier?.includes('_year') ? 12 : (item.duration_months || 1);
              date = addMonthsWithMonthEndClamp(date, months);
          }
          // 计算用纯年月日，最后拼接原始到期时间的时分（避免影响 addMonthsWithMonthEndClamp 的正则匹配）
          const t = new Date(memberInfo.currentMemberEndTime);
          const hh = String(t.getHours()).padStart(2, '0');
          const mm = String(t.getMinutes()).padStart(2, '0');
          return `${date} ${hh}:${mm}`;
      })()
      : '';

  const getModelPriceList = useCallback((membershipTier: MembershipTier) => {
    const nanoBanana2 = imagePricingRules.find((item) => item.model_id === 'gemini-3.1-flash-image-preview');
    const nanoBananaPro = imagePricingRules.find((item) => item.model_id === 'gemini-3-pro-image-preview');
    const seedream5Pro = imagePricingRules.find((item) => item.model_id === 'doubao-seedream-5-0-pro-260628');
    const gptImage2 = imagePricingRules.find((item) => item.model_id === 'gpt-image-2');
    const gptImage2_5 = imagePricingRules.find((item) => item.model_id === 'gpt-image-2.5-flare');
    const layer = imagePricingRules.find((item) => item.model_id === 'layer_decomposition');
    const HD = imagePricingRules.find((item) => item.model_id === 'jimeng-upscale');

    return [
      getResolutionTierPrice(nanoBanana2, membershipTier),
      getResolutionTierPrice(nanoBananaPro, membershipTier),
      getResolutionTierPrice(seedream5Pro, membershipTier),
      getQualityTierPrice(gptImage2_5, 'low', `Krene Image 2.5 标准`, membershipTier),
      getQualityTierPrice(gptImage2_5, 'medium', 'Krene Image 2.5 精细', membershipTier),
      getQualityTierPrice(gptImage2_5, 'high', 'Krene Image 2.5 超精', membershipTier),
      getQualityTierPrice(gptImage2_5, 'xhigh', 'Krene Image 2.5 xhigh', membershipTier),
      getQualityTierPrice(gptImage2_5, 'max', 'Krene Image 2.5 max', membershipTier),
      getQualityTierPrice(gptImage2, 'low', 'Krene Image 2 标准', membershipTier),
      getQualityTierPrice(gptImage2, 'medium', 'Krene Image 2 精细', membershipTier),
      getQualityTierPrice(gptImage2, 'high', 'Krene Image 2 超精', membershipTier),
      getResolutionTierPrice(layer, membershipTier),
      getResolutionTierPrice(HD, membershipTier),
    ].filter(Boolean).sort((a, b) => {
      const aIndex = DISPLAY_MODEL_ORDER.indexOf((a as ModelPriceItem).label as typeof DISPLAY_MODEL_ORDER[number]);
      const bIndex = DISPLAY_MODEL_ORDER.indexOf((b as ModelPriceItem).label as typeof DISPLAY_MODEL_ORDER[number]);
      return aIndex - bIndex;
    }) as ModelPriceItem[];
  }, [imagePricingRules]);

  // 会员身份徽标：queue 为空时只显示徽标不带 Tooltip、不可点
  const renderVipLeve = (clickable: boolean) => (
      <div
          className="vip-leve"
          onClick={clickable ? () => {
              trackMemberIdentityClick({ page_id: window.location.href });
              setMemberIdentityOpen(true);
          } : undefined}
      >
        <div className="member-level-badge">
          <div
              className="member-lever"
              style={{
                background: currentMember()?.buttonColor,
                color: memberInfo?.membershipTier === 'free' ? 'var(--c-text-primary)' : 'var(--c-on-color)',
              }}
          >
            {memberInfo?.membershipTier?.split('_')[0]}
          </div>
        </div>
      </div>
  );

  return (
    <>
    <Dialog
      open={open}
      fullScreen={isMobile}
      onClose={() => {
        onClose();
        reportStayTime(packages);
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: 'none',
          borderRadius: isMobile ? '0' : '20px',
        },
        '& .MuiDialogContent-root': {
          padding: '0',
        },
      }}
    >
      <DialogContent>
        <div className="payment-container">
          <div className="title">
            <div />
            <span>水滴详情</span>
            <span
              onClick={() => {
                onClose();
                reportStayTime(packages);
              }}
            >
              <CloseIcon />
            </span>
          </div>
          <div className="member-content">
            <div className="box-contain pc-only box-contain-compact">
              <div className="member-label">会员身份：</div>
              <div className="dropInfoContainer">
                <div className="dropInfo">
                  {hasMembershipQueue && !isMobile ? (
                  <Tooltip
                    open={!memberIdentityOpen}
                    title="点击查看会员身份"
                    placement="bottom"
                    arrow
                  >
                    {renderVipLeve(canClickIdentity)}
                  </Tooltip>
                  ) : renderVipLeve(canClickIdentity)}

                  <span className="drop-info-label">剩余水滴: </span>
                  <div className="box-flex drop-count">
                    <img src={droplet} className="drop-icon" alt="droplet" />
                    <AnimatedNumber value={(memberInfo?.flowingWater || 0) + (memberInfo?.memberDrops || 0) + (memberInfo?.supplyDrop || 0) + (memberInfo?.otherDrops || 0)} />
                  </div>
                  <div className="drop-category-badge">
                    <span className="drop-type-label">免费水滴</span>
                    <span className="drop-type-value">
                      <AnimatedNumber value={memberInfo?.flowingWater || 0} /> / <span style={{ color: 'var(--c-text-secondary)' }}>200</span>
                    </span>
                    <span className="drop-type-label" style={{ marginLeft: '15px' }}>会员水滴</span>
                    <span className="drop-type-value">
                      <AnimatedNumber value={memberInfo?.memberDrops || 0} />
                    </span>
                    <span className="drop-type-label" style={{ marginLeft: '15px' }}>补给水滴</span>
                    <span className="drop-type-value">
                      <AnimatedNumber value={memberInfo?.supplyDrop || 0} />
                    </span>
                    {memberInfo?.otherDrops ? (
                      <>
                        <span className="drop-type-label" style={{ marginLeft: '15px' }}>其它水滴</span>
                        <span className="drop-type-value">
                          <AnimatedNumber value={memberInfo?.otherDrops || 0} />
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div style={{ display: 'flex' }}>
                  <div
                    className="viewDropInfo"
                    onClick={() => {
                      trackConsumptionRecordClick({ page_id: window.location.href });
                      setOpenBillDialog(true);
                    }}
                  >
                    查看账单
                  </div>
                  &nbsp;
                  <div
                    className="viewDropInfo"
                    onClick={() => {
                      const totalDrops =
                        (memberInfo?.flowingWater || 0) + (memberInfo?.memberDrops || 0) + (memberInfo?.otherDrops || 0);
                      trackWaterdropDetailClick({
                        page_id: window.location.href,
                        subscription_plan: memberInfo?.membershipTier,
                        waterdrop_amount: totalDrops,
                      });
                      setOpenDropWaterDialog(true);
                    }}
                  >
                    水滴详情
                  </div>
                </div>
              </div>
            </div>

            <div className="mobile-only">
              <div className="box-mobile-info">
              <div className="bmi-top-row">
                <div className="bmi-member-group">
                  <span className="bmi-member-label">会员身份：</span>
                  {hasMembershipQueue && isMobile ? (
                  <Tooltip
                    open={!memberIdentityOpen}
                    title="点击查看会员身份"
                    placement="top"
                    arrow
                  >
                    <span className="bmi-tier-badge" onClick={() => {
                        trackMemberIdentityClick({ page_id: window.location.href });
                        setMemberIdentityOpen(true);
                    }}>
                      {memberInfo?.membershipTier?.split('_')[0] || 'FREE'}
                    </span>
                  </Tooltip>
                  ) : (
                    <span className="bmi-tier-badge" onClick={() => {
                      if (canClickIdentity) {
                        trackMemberIdentityClick({ page_id: window.location.href });
                        setMemberIdentityOpen(true);
                      }
                    }}>{memberInfo?.membershipTier?.split('_')[0] || 'FREE'}</span>
                  )}
                </div>
                <div className="bmi-drops-group">
                  <span className="bmi-drops-label">剩余水滴：</span>
                  <img src={droplet} className="bmi-drop-icon" alt="" />
                  <span className="bmi-drops-count">
                    <AnimatedNumber value={(memberInfo?.flowingWater || 0) + (memberInfo?.memberDrops || 0) + (memberInfo?.supplyDrop || 0) + (memberInfo?.otherDrops || 0)} />
                  </span>
                </div>
              </div>

              <div className="bmi-stats-row">
                <div className="bmi-stat-card">
                  <span className="bmi-stat-label">每日水滴</span>
                  <span className="bmi-stat-value"><AnimatedNumber value={memberInfo?.flowingWater || 0} /><span style={{color: 'var(--c-text-secondary)'}}>/200</span></span>
                </div>
                <div className="bmi-stat-card">
                  <span className="bmi-stat-label">会员水滴</span>
                  <span className="bmi-stat-value"><AnimatedNumber value={memberInfo?.memberDrops || 0} /></span>
                </div>
                <div className="bmi-stat-card">
                  <span className="bmi-stat-label">补给水滴</span>
                  <span className="bmi-stat-value"><AnimatedNumber value={memberInfo?.supplyDrop || 0} /></span>
                </div>
                <div className="bmi-stat-card">
                  <span className="bmi-stat-label">其他水滴</span>
                  <span className="bmi-stat-value"><AnimatedNumber value={memberInfo?.otherDrops || 0} /></span>
                </div>
              </div>

              <div className="bmi-btn-row">
                <div className="bmi-btn bmi-btn--dark" onClick={() => {
                  trackConsumptionRecordClick({ page_id: window.location.href });
                  setOpenBillDialog(true);
                }}>
                  查看账单
                </div>
                <div className="bmi-btn bmi-btn--dark" onClick={() => {
                  const totalDrops =
                    (memberInfo?.flowingWater || 0) + (memberInfo?.memberDrops || 0) + (memberInfo?.otherDrops || 0);
                  trackWaterdropDetailClick({
                    page_id: window.location.href,
                    subscription_plan: memberInfo?.membershipTier,
                    waterdrop_amount: totalDrops,
                  });
                  setOpenDropWaterDialog(true);
                }}>
                  水滴详情
                </div>
              </div>
            </div>
            </div>

            <div className="paymentTab">
              <div
                onClick={() => {
                  reportStayTime(packages, true);
                  setPackages(1);
                }}
                style={{
                  backgroundColor: packages === 1 ? 'var(--c-bg)' : 'transparent',
                  color: 'var(--c-text-primary)',
                }}
              >
                水滴补给包
              </div>
              <div
                onClick={() => {
                  const queryParams = new URLSearchParams(window.location.search);
                  const boardId = queryParams.get('boardId');
                  trackBillingPopupShow({
                    billing_popup_from: 'subscription_nav_bar',
                    board_id: boardId,
                  });
                  reportStayTime(packages, true);
                  setPackages(2);
                }}
                style={{
                  backgroundColor: packages === 2 ? 'var(--c-bg)' : 'transparent',
                  color: 'var(--c-text-primary)',
                }}
              >
                月会员套餐
              </div>
              <div
                onClick={() => {
                  const page_id = window.location.href;
                  const device_id = getLocalStorage('device_id') || '';
                  trackYearlySubscriptionPopupShow({ device_id, page_id });
                  reportStayTime(packages, true);
                  setPackages(3);
                }}
                style={{
                  backgroundColor: packages === 3 ? 'var(--c-bg)' : 'transparent',
                  color: 'var(--c-text-primary)',
                }}
              >
                年会员套餐
              </div>
              <img
                className="new-years-badge"
                src={NewYears}
                alt=""
              />
            </div>

            <div className="box-contain" style={{ paddingTop: 20, paddingBottom: 20 }}>
              {packages === 1 && (
                <div style={{ height: 'auto' }}>
                  <WaterDropBag handleSelectPaymentOpen={handleSelectPaymentOpen} handleCallBackPlan={handleCallBackPlan} />
                </div>
              )}

              {(packages === 2 || packages === 3) && (
                <div
                  className="plan-card-row"
                  style={{ width: packages === 2 ? 'auto' : '920px' }}
                >
                  {(packages === 3 ? plansYears : plans).map((plan, index) => {
                    const mi = memberIndex();
                    const userLevel = mi <= 3 ? mi : mi - 3;
                    const cardLevel = index;
                    const isYearUserOnMonthTab = packages === 2 && mi >= 4;
                    const isDowngrade = cardLevel < userLevel;
                    const isCurrentPlan = plan.membershipTier === memberInfo?.membershipTier;
                    const isFreePlanCard = plan.membershipTier === 'free' || plan.membershipTier === 'free_year';
                    const isCardDisabled = isYearUserOnMonthTab || isDowngrade || (isCurrentPlan && isFreePlanCard) || (hasMonthlyDrops?.payload?.membershipTier) || (hasRenewalActivate?.payload?.membershipTier);

                    return (
                      <div
                        className="setMeal"
                        style={{
                          border: `2px solid ${plan.shawdowColor}`,
                          boxShadow: `0 4px 12px ${plan.shawdowColor}`,
                          display: plan.membershipTier === 'free_year' ? 'none' : 'block',
                        }}
                        key={plan.membershipTier}
                      >
                        <div className="plan">
                          <div
                            style={{
                              background: plan.color,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }}
                          >
                            {plan.plan}
                          </div>
                        </div>
                        <div className="original_price" style={{ visibility: plan.origin_price ? 'visible' : 'hidden' }}>
                          ¥{plan.origin_price.toFixed(2)}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div
                            className="price"
                            style={{
                              background: plan.color,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }}
                          >
                            ¥{plan.price.toFixed(2)}
                            <span>/{plan.unit}</span>
                          </div>
                          {plan.discount ? (
                            <div className="discount-badge">
                              {t('discount', { discount: plan.discount })}
                            </div>
                          ) : null}
                        </div>

                        <div
                          className="expire-bar"
                          style={{
                            cursor: 'pointer',
                            background:
                                isCurrentPlan && currentMemberAllExpireDate
                                ? 'linear-gradient(90deg, var(--c-bg) 0%, var(--c-bg-secondary) 25%, var(--c-bg-secondary) 75%, var(--c-bg) 100%)'
                                : 'transparent',
                          }}
                        >
                          {isCurrentPlan && !isFreePlanCard && currentMemberAllExpireDate && (
                            <span onClick={() => { if (canClickIdentity) setMemberIdentityOpen(true); }} className="expire-text" style={{ color: plan.color }}>
                              {currentMemberAllExpireDate} 到期
                            </span>
                          )}
                        </div>

                        <Button
                          variant="contained"
                          onClick={() => {
                            trackSubscriptionPlanSelect({ subscription_plan: plan.membershipTier, switch_from: memberInfo?.membershipTier });
                            setCallBackPlan({ plan: plan.membershipTier, amount: plan.price, dropAmount: plan.total, productId: plan.productId });
                            if (memberInfo?.membershipTier === 'free') {
                              setSelectPaymentOpen(true);
                            } else {
                              setCheckBoxDialogOpen(true);
                            }
                          }}
                          disabled={isCardDisabled}
                          sx={{
                            background: isCardDisabled ? 'var(--c-border-light)' : plan.buttonColor,
                            color: index === 0 ? 'var(--c-text-secondary)' : 'var(--c-on-color)',
                            textTransform: 'none',
                            fontWeight: 500,
                            width: '100%',
                            height: '40px',
                            fontSize: '18px',
                            borderRadius: '10px',
                          }}
                        >
                          {(hasMonthlyDrops?.payload?.membershipTier || hasRenewalActivate?.payload.membershipTier) ? '处理中' : (isCurrentPlan ? (isFreePlanCard ? t('current_plan1') : t('renew')) : t('upgrade'))}
                        </Button>

                        <div className="total-container">
                          <Tooltip
                            title="不同于多数同类产品每月清空会员积分，Krene 的会员水滴支持跨周期累积！
                                    只要您在会员到期前及时「续购」或「升级」、保持会员身份连续生效，所有的会员水滴都会永久保留、长期有效，不会自动过期清零，可随时随心使用。
                                    注意：若未在到期前及时「续购」或「升级」会员，而是在过期后重新开通会员，已失效的会员水滴将无法恢复。"
                            placement="top"
                            arrow
                            slotProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: 'var(--c-inverse-surface)',
                                  '& .MuiTooltip-arrow': {
                                    color: 'var(--c-inverse-surface)',
                                  },
                                },
                              },
                            }}
                          >
                            <div className="accumulate-badge">
                              可累积
                              <DropTipsIcon />
                            </div>
                          </Tooltip>
                          <div className="text">
                            {packages === 2 ? '每月赠送' : '每年赠送'}
                          </div>
                          <div className="num">
                            <img src={droplet} className="drop-icon" alt="" />
                            {plan.total.toLocaleString('zh-CN')}
                          </div>
                        </div>

                        <div>
                          <div className="introduce">{plan.introduce}</div>
                          {plan.function.map((text, idx) => (
                            <div className="function" key={idx}>
                              <BallIconBlack /> &nbsp;{text}
                              {text === '会员水滴消耗减免' &&
                                  <div className="generate-badge">生图总量 {plan.dropDiscount}</div>
                              }
                            </div>
                          ))}
                          <div className="nano-banana-wrapper">
                            {getModelPriceList(plan.membershipTier as MembershipTier).map((item) => (
                              <div className="nano-banana-row" key={item.label}>
                                <div className="nano-banana-title"><BallIconWhite/> &nbsp;{item.label}</div>
                                <div className="nano-banana-price">
                                  {!isFreePlanCard && <span className="nano-banana-price-original">{item.originalPrice}</span>}
                                  <TinyDropIcon/> {item.currentPrice}起
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {packages === 1 && (
                <div className="invoiceTips">
                  补给水滴包有效期为100天，发放到补给水滴栏，每次新获得补给水滴，剩余水滴重置有效期为100天。
                </div>
            )}

            <div className="box-contain" style={{ paddingTop: '0px' }}>
              <div className="giftCode">
                <div className="gift-code-title">🎁 {t('have_gift_code')}</div>

                <div className="gift-code-row">
                  <TextField
                    fullWidth
                    translate="no"
                    placeholder={t('enter_gift_code')}
                    variant="outlined"
                    size="small"
                    value={giftCode}
                    onChange={(e) => {
                      setGiftCode(e.target.value);
                    }}
                    sx={{
                      width: '90%',
                    }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    loading={!!hasGiftCodeRedeem}
                    onClick={redeemGiftCode}
                    sx={{
                      backgroundColor: 'var(--c-violet-soft)',
                      color: 'var(--c-on-color)',
                      textTransform: 'none',
                      fontWeight: 500,
                      width: '9%',
                      height: 38,
                      '&:hover': {
                        backgroundColor: 'var(--c-violet-soft)',
                      },
                    }}
                  >
                    {t('redeem')}
                  </Button>
                </div>
                {RedeemMessage && <div className="redeem-message">{RedeemMessage}</div>}
              </div>
            </div>
            <div className="box-contain" style={{ paddingTop: 0 }}>
              <PaymentRules />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <MemberIdentityDialog open={memberIdentityOpen} onClose={() => setMemberIdentityOpen(false)} membershipQueue={membershipQueueRaw?.queue} currentMemberEndTime={memberInfo?.currentMemberEndTime} />
    </>
  );
}
